import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { updateCurrentClient as advisorUpdateCurrentClient } from '../../../AdviserArea/AdviserAreaActions';
import { updateCurrentClient as clientUpdateCurrentClient } from '../../../ConsumerArea/ConsumerAreaActions';
import { authFetch } from '../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../CommonUtils/httpBodyOptions';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';
import { DependentType, PersonalInfoDTO } from './usePersonalInfoQuery';
import { isConsumerArea as selectIsConsumerArea } from '../../../CommonSelectors/CommonAreaSelector';
import { useClientId } from '../../../_hooks/useClientId';
import { PersonalInfoContext } from '../PersonalInfo.meta';

export const UPDATE_PERSONAL_INFO_ENDPOINT = '/Consumer/PersonalInfoUpdate';

export const UPDATE_PERSONAL_INFO_MK = 'updatePersonalInfo';

interface User {
  FirstName: string;
  LastName: string;
  Dob: string;
}

interface Beneficiary {
  Id: string | null;
  FirstName: string;
  Type: DependentType;
}

interface Dependent extends Beneficiary {
  Dob: string;
  IsDependent: boolean;
}

export interface UpdatePersonalInfoBody {
  KeepDeletedPartnerAssociations: boolean | null;

  Principal: User;

  Partner: User | null;

  Taxes: {
    FilingState: number;
    FilingStatus: number;
  };

  Dependents: (Dependent | Beneficiary)[];
}

interface UpdatePersonalInfoParams {
  body: UpdatePersonalInfoBody;
}

const updatePersonalInfo = (clientId: number) => ({
  body,
}: UpdatePersonalInfoParams): Promise<PersonalInfoDTO> => {
  return authFetch(urlWithParams(UPDATE_PERSONAL_INFO_ENDPOINT, { clientId }), {
    method: 'POST',
    ...httpBodyOptions(body, true),
  });
};

interface MutationParams {
  context?: PersonalInfoContext;
}

export const usePersonalInfoUpdateMutation = ({ context }: MutationParams) => {
  const clientId = useClientId();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isConsumerArea = useSelector(selectIsConsumerArea);

  return useMutation(updatePersonalInfo(clientId), {
    mutationKey: UPDATE_PERSONAL_INFO_MK,

    onSuccess(data) {
      if (context !== PersonalInfoContext.CharitableGivingWorkflow) {
        queryClient.setQueryData<PersonalInfoDTO>(queryKeys.personalInfo(clientId), cache => ({
          ...cache,
          ...data,
        }));
      }

      const updateClient = {
        Dob: data.Principal.Dob,
        IsCanadian: data.IsCanadian,
        ...(data.Partner
          ? {
              PartnerDob: data.Partner.Dob,
              PartnerName: `${data.Partner.FirstName} ${data.Partner.LastName}`,
            }
          : { PartnerName: null, PartnerDob: null }),
      };

      if (isConsumerArea) {
        dispatch(clientUpdateCurrentClient(updateClient));
      } else {
        dispatch(advisorUpdateCurrentClient(updateClient));
      }
    },
  });
};
